/**
 * PartnerVendorContext — applies a partner vendor's theme when the user is
 * on `/vendors/:slug` and reverts on unmount.
 *
 * Why this exists:
 *   BHG's CSS uses shadcn-style `hsl(var(--token))` with HSL triples stored
 *   as CSS variables. Vendor themes are stored as hex in Supabase. This context
 *   converts hex → HSL, writes to `:root`, loads vendor fonts + favicon, and
 *   restores BHG defaults on page exit. Providers must wrap the app below
 *   `<BrowserRouter>` because it relies on `useLocation`.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { hexToHslString } from "@/utils/generateVendorTheme";

// ---- Types -----------------------------------------------------------------

export interface PartnerVendor {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  website_url: string | null;
  store_url: string | null;
  trustpilot_url: string | null;
  support_email: string | null;
  description: string | null;
  founder_story: string | null;
  mission_statement: string | null;
  founding_year: number | null;
  location: string | null;
  testing_lab: string | null;
  shipping_speed: string | null;
  shipping_info: string | null;
  payment_methods: string[] | null;
  trustpilot_score: number | null;
  trustpilot_review_count: number | null;
  affiliate_ref_param: string | null;
  affiliate_cookie_days: number | null;
  default_coupon_code: string | null;
  default_coupon_desc: string | null;
  status: "pending" | "active" | "inactive" | "archived";
  is_featured: boolean;
  seo_title: string | null;
  seo_description: string | null;
  canonical_url: string | null;
  noindex: boolean;
}

export interface PartnerVendorTheme {
  id: string;
  partner_vendor_id: string;
  color_primary: string;
  color_primary_foreground: string;
  color_secondary: string;
  color_secondary_foreground: string;
  color_accent: string;
  color_accent_foreground: string;
  color_background: string;
  color_foreground: string;
  color_muted: string;
  color_muted_foreground: string;
  color_card: string;
  color_card_foreground: string;
  color_border: string;
  color_ring: string;
  dark_colors: Record<string, string> | null;
  font_heading: string;
  font_body: string;
  font_mono: string | null;
  border_radius: string;
  logo_url: string | null;
  logo_dark_url: string | null;
  favicon_url: string | null;
  og_image_url: string | null;
  hero_image_url: string | null;
}

interface PartnerVendorContextValue {
  vendor: PartnerVendor | null;
  theme: PartnerVendorTheme | null;
  isPartnerVendorPage: boolean;
  isLoading: boolean;
  error: string | null;
}

const PartnerVendorContext = createContext<PartnerVendorContextValue>({
  vendor: null,
  theme: null,
  isPartnerVendorPage: false,
  isLoading: false,
  error: null,
});

// CSS tokens we control. Keys map to `--<token>` CSS variables; values are the
// corresponding keys on `PartnerVendorTheme`.
const COLOR_TOKEN_MAP: Array<{ cssVar: string; themeKey: keyof PartnerVendorTheme }> = [
  { cssVar: "--background",            themeKey: "color_background" },
  { cssVar: "--foreground",            themeKey: "color_foreground" },
  { cssVar: "--primary",               themeKey: "color_primary" },
  { cssVar: "--primary-foreground",    themeKey: "color_primary_foreground" },
  { cssVar: "--secondary",             themeKey: "color_secondary" },
  { cssVar: "--secondary-foreground",  themeKey: "color_secondary_foreground" },
  { cssVar: "--accent",                themeKey: "color_accent" },
  { cssVar: "--accent-foreground",     themeKey: "color_accent_foreground" },
  { cssVar: "--muted",                 themeKey: "color_muted" },
  { cssVar: "--muted-foreground",      themeKey: "color_muted_foreground" },
  { cssVar: "--card",                  themeKey: "color_card" },
  { cssVar: "--card-foreground",       themeKey: "color_card_foreground" },
  { cssVar: "--border",                themeKey: "color_border" },
  { cssVar: "--ring",                  themeKey: "color_ring" },
];

// ---- Provider --------------------------------------------------------------

export function PartnerVendorProvider({ children }: { children: ReactNode }) {
  const location = useLocation();

  // Match /vendors/:slug (plural, with slug). Exclude bare /vendors.
  const slug = useMemo(() => {
    const match = location.pathname.match(/^\/vendors\/([a-z0-9][a-z0-9-]*[a-z0-9])(?:\/|$)/i);
    return match ? match[1].toLowerCase() : null;
  }, [location.pathname]);

  const isPartnerVendorPage = slug !== null;

  const [vendor, setVendor] = useState<PartnerVendor | null>(null);
  const [theme, setTheme] = useState<PartnerVendorTheme | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch vendor + theme when slug changes
  useEffect(() => {
    if (!slug) {
      setVendor(null);
      setTheme(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const { data: vendorRow, error: vendorErr } = await supabase
          .from("partner_vendors" as any)
          .select("*")
          .eq("slug", slug)
          .eq("status", "active")
          .maybeSingle();

        if (cancelled) return;
        if (vendorErr) throw vendorErr;
        if (!vendorRow) {
          setVendor(null);
          setTheme(null);
          return;
        }

        setVendor(vendorRow as unknown as PartnerVendor);

        const { data: themeRow, error: themeErr } = await supabase
          .from("partner_vendor_themes" as any)
          .select("*")
          .eq("partner_vendor_id", (vendorRow as any).id)
          .maybeSingle();

        if (cancelled) return;
        if (themeErr) throw themeErr;
        setTheme((themeRow as unknown as PartnerVendorTheme) ?? null);
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message || "Failed to load partner vendor");
          setVendor(null);
          setTheme(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Apply theme CSS variables
  useEffect(() => {
    if (!isPartnerVendorPage || !theme) return;

    const root = document.documentElement;
    const saved: Record<string, string> = {};

    // 1. Colors — convert hex → "H S% L%" shadcn format
    for (const { cssVar, themeKey } of COLOR_TOKEN_MAP) {
      const hex = theme[themeKey] as string | null;
      if (!hex) continue;
      saved[cssVar] = root.style.getPropertyValue(cssVar);
      try {
        root.style.setProperty(cssVar, hexToHslString(hex));
      } catch {
        /* skip invalid values */
      }
    }

    // 2. Radius (raw rem value)
    if (theme.border_radius) {
      saved["--radius"] = root.style.getPropertyValue("--radius");
      root.style.setProperty("--radius", theme.border_radius);
    }

    // 3. Marker class so CSS can scope vendor-only overrides
    root.classList.add("partner-vendor-active");
    root.setAttribute("data-partner-vendor-slug", vendor?.slug ?? "");

    return () => {
      for (const { cssVar } of COLOR_TOKEN_MAP) {
        if (saved[cssVar]) root.style.setProperty(cssVar, saved[cssVar]);
        else root.style.removeProperty(cssVar);
      }
      if ("--radius" in saved) {
        if (saved["--radius"]) root.style.setProperty("--radius", saved["--radius"]);
        else root.style.removeProperty("--radius");
      }
      root.classList.remove("partner-vendor-active");
      root.removeAttribute("data-partner-vendor-slug");
    };
  }, [isPartnerVendorPage, theme, vendor?.slug]);

  // Load vendor fonts + preconnect
  useEffect(() => {
    if (!isPartnerVendorPage || !theme) return;

    const preconnect1 = document.createElement("link");
    preconnect1.rel = "preconnect";
    preconnect1.href = "https://fonts.googleapis.com";
    preconnect1.setAttribute("data-partner-vendor", "1");
    document.head.appendChild(preconnect1);

    const preconnect2 = document.createElement("link");
    preconnect2.rel = "preconnect";
    preconnect2.href = "https://fonts.gstatic.com";
    preconnect2.crossOrigin = "anonymous";
    preconnect2.setAttribute("data-partner-vendor", "1");
    document.head.appendChild(preconnect2);

    const families = [theme.font_heading, theme.font_body]
      .filter(Boolean)
      .map((f) => `family=${encodeURIComponent(f).replace(/%20/g, "+")}:wght@400;500;600;700`)
      .join("&");
    const fontUrl = `https://fonts.googleapis.com/css2?${families}&display=swap`;

    const fontLink = document.createElement("link");
    fontLink.id = "partner-vendor-fonts";
    fontLink.rel = "stylesheet";
    fontLink.href = fontUrl;
    document.head.appendChild(fontLink);

    const root = document.documentElement;
    const prevHeading = root.style.getPropertyValue("--font-heading");
    const prevBody = root.style.getPropertyValue("--font-body");
    root.style.setProperty("--font-heading", `'${theme.font_heading}', 'Geist', 'Inter', system-ui, sans-serif`);
    root.style.setProperty("--font-body", `'${theme.font_body}', 'Inter', system-ui, sans-serif`);

    return () => {
      preconnect1.remove();
      preconnect2.remove();
      const existing = document.getElementById("partner-vendor-fonts");
      if (existing) existing.remove();
      if (prevHeading) root.style.setProperty("--font-heading", prevHeading);
      else root.style.removeProperty("--font-heading");
      if (prevBody) root.style.setProperty("--font-body", prevBody);
      else root.style.removeProperty("--font-body");
    };
  }, [isPartnerVendorPage, theme]);

  // Favicon swap
  useEffect(() => {
    if (!isPartnerVendorPage || !theme?.favicon_url) return;

    const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const original = existing?.href;

    if (existing) {
      existing.href = theme.favicon_url;
    } else {
      const el = document.createElement("link");
      el.rel = "icon";
      el.href = theme.favicon_url;
      el.setAttribute("data-partner-vendor", "1");
      document.head.appendChild(el);
    }

    return () => {
      const el = document.querySelector<HTMLLinkElement>('link[rel="icon"][data-partner-vendor="1"]');
      if (el) el.remove();
      if (existing && original) existing.href = original;
    };
  }, [isPartnerVendorPage, theme?.favicon_url]);

  const value = useMemo<PartnerVendorContextValue>(
    () => ({ vendor, theme, isPartnerVendorPage, isLoading, error }),
    [vendor, theme, isPartnerVendorPage, isLoading, error]
  );

  return <PartnerVendorContext.Provider value={value}>{children}</PartnerVendorContext.Provider>;
}

export function usePartnerVendor() {
  return useContext(PartnerVendorContext);
}
